import React, { FormEvent, useContext, useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { Checkbox, DefaultButton, Dialog, FontIcon, Stack, Text, TextField } from '@fluentui/react'
import { useBoolean } from '@fluentui/react-hooks'
import { ThumbDislike20Filled, ThumbLike20Filled } from '@fluentui/react-icons'
import DOMPurify from 'dompurify'
import remarkGfm from 'remark-gfm'
import supersub from 'remark-supersub'
import Plot from 'react-plotly.js'
import { AskResponse, Citation, Feedback, historyMessageFeedback } from '../../api'
import { XSSAllowTags, XSSAllowAttributes } from '../../constants/sanatizeAllowables'
import { AppStateContext } from '../../state/AppProvider'

import { parseAnswer } from './AnswerParser'

import styles from './Answer.module.css'

interface Props {
  answer: AskResponse
}

export const Answer = ({ answer }: Props) => {
  const initializeAnswerFeedback = (answer: AskResponse) => {
    if (answer.message_id == undefined) return undefined
    if (answer.feedback == undefined) return undefined
    if (answer.feedback.split(',').length > 1) return Feedback.Negative
    if (Object.values(Feedback).includes(answer.feedback)) return answer.feedback
    return Feedback.Neutral
  }

  const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] = useBoolean(false)
  const filePathTruncationLimit = 50

  const parsedAnswer = useMemo(() => parseAnswer(answer), [answer])
  const [chevronIsExpanded, setChevronIsExpanded] = useState(isRefAccordionOpen)
  const [feedbackState, setFeedbackState] = useState(initializeAnswerFeedback(answer))
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false)
  const [showReportInappropriateFeedback, setShowReportInappropriateFeedback] = useState(false)
  const [negativeFeedbackList, setNegativeFeedbackList] = useState<Feedback[]>([])
  const appStateContext = useContext(AppStateContext)
  const FEEDBACK_ENABLED = true
  const SANITIZE_ANSWER = appStateContext?.state.frontendSettings?.sanitize_answer
  const [otherFeedbackText, setOtherFeedbackText] = useState('');
  const [supportedFeaturesExpanded, setSupportedFeaturesExpanded] = useState(false);

  const handleChevronClick = () => {
    setChevronIsExpanded(!chevronIsExpanded)
    toggleIsRefAccordionOpen()
  }

  useEffect(() => {
    setChevronIsExpanded(isRefAccordionOpen)
  }, [isRefAccordionOpen])

  const toggleIsSupportedFeaturesOpen = () => {
    setSupportedFeaturesExpanded(!supportedFeaturesExpanded);
  };

  const handleSupportedFeaturesChevronClick = () => {
    setSupportedFeaturesExpanded(!supportedFeaturesExpanded);
  };

  useEffect(() => {
    if (answer.message_id == undefined) return

    let currentFeedbackState
    if (appStateContext?.state.feedbackState && appStateContext?.state.feedbackState[answer.message_id]) {
      currentFeedbackState = appStateContext?.state.feedbackState[answer.message_id]
    } else {
      currentFeedbackState = initializeAnswerFeedback(answer)
    }
    setFeedbackState(currentFeedbackState)
  }, [appStateContext?.state.feedbackState, feedbackState, answer.message_id])
    const onLikeResponseClicked = async () => {
    if (answer.message_id == undefined) return

    let newFeedbackState = feedbackState
    // Set or unset the thumbs up state
    if (feedbackState == Feedback.Positive) {
      newFeedbackState = Feedback.Neutral
    } else {
      newFeedbackState = Feedback.Positive
    }
    appStateContext?.dispatch({
      type: 'SET_FEEDBACK_STATE',
      payload: { answerId: answer.message_id, feedback: newFeedbackState }
    })
    setFeedbackState(newFeedbackState)

    // Update message feedback in db
    await historyMessageFeedback(answer.message_id, newFeedbackState, answer.tracer)
  }

  const onDislikeResponseClicked = async () => {
    if (answer.message_id == undefined) return

    let newFeedbackState = feedbackState
    if (feedbackState === undefined || feedbackState === Feedback.Neutral || feedbackState === Feedback.Positive) {
      newFeedbackState = Feedback.Negative
      setFeedbackState(newFeedbackState)
      setIsFeedbackDialogOpen(true)
    } else {
      // Reset negative feedback to neutral
      newFeedbackState = Feedback.Neutral
      setFeedbackState(newFeedbackState)
      await historyMessageFeedback(answer.message_id, Feedback.Neutral, answer.tracer)
    }
    appStateContext?.dispatch({
      type: 'SET_FEEDBACK_STATE',
      payload: { answerId: answer.message_id, feedback: newFeedbackState }
    })
  }

  const updateFeedbackList = (ev?: FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
    if (answer.message_id == undefined) return
    const selectedFeedback = (ev?.target as HTMLInputElement)?.id as Feedback

    let feedbackList = negativeFeedbackList.slice()
    if (checked) {
      feedbackList.push(selectedFeedback)
    } else {
      feedbackList = feedbackList.filter(f => f !== selectedFeedback)
    }

    setNegativeFeedbackList(feedbackList)
  }

  const handleOtherTextChange = (
      event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue?: string
  ) => {
    setOtherFeedbackText(newValue || "");
  };

  const onSubmitNegativeFeedback = async () => {
    if (answer.message_id == undefined) return
    let feedback = negativeFeedbackList.join(', ');

    if (otherFeedbackText.trim()) {
      feedback += `, Other: ${otherFeedbackText}`;
    }
    await historyMessageFeedback(answer.message_id, feedback, answer.tracer)
    resetFeedbackDialog()
  }

  const resetFeedbackDialog = () => {
    setIsFeedbackDialogOpen(false)
    setShowReportInappropriateFeedback(false)
    setNegativeFeedbackList([])
    setOtherFeedbackText('')
  }

    const customCheckboxStyles = {
        checkmark: { backgroundColor: '#ff8c00', borderColor: '#ff8c00' },  // Change checkmark color to orange when selected
        checkbox: { borderColor: '#000000' },  // Keep border color always black
        root: {
            selectors: {
                '&.is-checked .ms-Checkbox-checkbox': {
                    borderColor: '#000000', // Keep border black when checked
                    backgroundColor: 'transparent', // Remove blue background when checked
                },
                '&:hover .ms-Checkbox-checkbox': {
                    borderColor: '#000000',  // Keep border black on hover
                },
            },
        },
    };
    const UnhelpfulFeedbackContent = () => {
        return (
            <>
                <div>Why wasn't this response helpful?</div>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Checkbox
                        label="Incorrect"
                        id={Feedback.IncorrectAnswer}
                        defaultChecked={negativeFeedbackList.includes(Feedback.IncorrectAnswer)}
                        onChange={updateFeedbackList}
                        styles={customCheckboxStyles}
                    />
                    <Checkbox
                        label="Unclear"
                        id={Feedback.UnclearAnswer}
                        defaultChecked={negativeFeedbackList.includes(Feedback.UnclearAnswer)}
                        onChange={updateFeedbackList}
                        styles={customCheckboxStyles}
                    />
                    <Checkbox
                        label="Incomplete"
                        id={Feedback.Incomplete}
                        defaultChecked={negativeFeedbackList.includes(Feedback.Incomplete)}
                        onChange={updateFeedbackList}
                        styles={customCheckboxStyles}
                    />
                    <Checkbox
                        label="Too long"
                        id={Feedback.TooLong}
                        defaultChecked={negativeFeedbackList.includes(Feedback.TooLong)}
                        onChange={updateFeedbackList}
                        styles={customCheckboxStyles}

                    />
                    <Checkbox
                        label="Other"
                        id={Feedback.OtherUnhelpful}
                        defaultChecked={negativeFeedbackList.includes(Feedback.OtherUnhelpful)}
                        onChange={updateFeedbackList}
                        styles={customCheckboxStyles}
                    />
                </Stack>
            </>
        );
    };

  const ReportInappropriateFeedbackContent = () => {
    return (
      <>
        <div>
          The content is <span style={{ color: 'red' }}>*</span>
        </div>
        <Stack tokens={{ childrenGap: 4 }}>
          <Checkbox
            label="Hate speech, stereotyping, demeaning"
            id={Feedback.HateSpeech}
            defaultChecked={negativeFeedbackList.includes(Feedback.HateSpeech)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Violent: glorification of violence, self-harm"
            id={Feedback.Violent}
            defaultChecked={negativeFeedbackList.includes(Feedback.Violent)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Sexual: explicit content, grooming"
            id={Feedback.Sexual}
            defaultChecked={negativeFeedbackList.includes(Feedback.Sexual)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Manipulative: devious, emotional, pushy, bullying"
            defaultChecked={negativeFeedbackList.includes(Feedback.Manipulative)}
            id={Feedback.Manipulative}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Other"
            id={Feedback.OtherHarmful}
            defaultChecked={negativeFeedbackList.includes(Feedback.OtherHarmful)}
            onChange={updateFeedbackList}></Checkbox>
        </Stack>
      </>
    )
  }

  const components = {
    code({ node, ...props }: { node: any;[key: string]: any }) {
      let language
      if (props.className) {
        const match = props.className.match(/language-(\w+)/)
        language = match ? match[1] : undefined
      }
      const codeString = node.children[0].value ?? ''
      return (
        <SyntaxHighlighter style={nord} language={language} PreTag="div" {...props}>
          {codeString}
        </SyntaxHighlighter>
      )
    }
  }
    return (
        <>
            <Stack className={styles.answerContainer} tabIndex={0}>
                <Stack.Item>
                    <Stack horizontal grow>
                        <Stack.Item grow>
                            <ReactMarkdown
                                linkTarget="_blank"
                                remarkPlugins={[remarkGfm, supersub]}
                                children={
                                    SANITIZE_ANSWER
                                        ? DOMPurify.sanitize(parsedAnswer.answerText, {
                                            ALLOWED_TAGS: XSSAllowTags,
                                            ALLOWED_ATTR: XSSAllowAttributes,
                                        })
                                        : parsedAnswer.answerText.replace(/\\n/g, '\n')
                                }
                                className={styles.answerText}
                                components={components}
                            />
                        </Stack.Item>
                    </Stack>
                </Stack.Item>

                {/* Footer: Information Sources and Suggested Features */}
                <Stack className={styles.answerFooter}>
                    {/* Information Sources Section */}
                    {parsedAnswer.sources.length > 0 && (
                        <Stack.Item
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? toggleIsRefAccordionOpen() : null)}>
                            <Stack style={{ width: '100%' }}>
                                <Stack horizontal horizontalAlign="start" verticalAlign="center">
                                    <Text
                                        className={styles.accordionTitle}
                                        onClick={toggleIsRefAccordionOpen}
                                        aria-label="Open references"
                                        tabIndex={0}
                                        role="button">
                  <span>
                    {parsedAnswer.sources.length > 1
                        ? `${parsedAnswer.sources.length} Information Sources`
                        : '1 Information Sources'}
                  </span>
                                    </Text>
                                    <FontIcon
                                        className={styles.accordionIcon}
                                        onClick={handleChevronClick}
                                        iconName={chevronIsExpanded ? 'ChevronDown' : 'ChevronRight'}
                                    />
                                </Stack>
                            </Stack>
                        </Stack.Item>
                    )}
                    {chevronIsExpanded && (
                        <div className={styles.citationWrapper}>
                            <ul className={styles.sourceList}>
                                {parsedAnswer.sources.map((source, idx) => (
                                    <li key={idx} className={styles.citationContainer}>
                                        <a
                                            href={source}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={source}>
                                            {source}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Suggested Features Section */}
                    {parsedAnswer.supportedFeatures.length > 0 && (
                        <Stack className={styles.supportedFeaturesContainer}>
                            <Stack.Item
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? toggleIsSupportedFeaturesOpen() : null)}>
                                <Stack style={{ width: '100%' }}>
                                    <Stack horizontal horizontalAlign="start" verticalAlign="center">
                                        <Text
                                            className={styles.accordionTitle}
                                            onClick={toggleIsSupportedFeaturesOpen}
                                            aria-label="Open suggested features"
                                            tabIndex={0}
                                            role="button">
                                            <span>Available Knowledge Base Topics</span>
                                        </Text>
                                        <FontIcon
                                            className={styles.accordionIcon}
                                            onClick={handleSupportedFeaturesChevronClick}
                                            iconName={supportedFeaturesExpanded ? 'ChevronDown' : 'ChevronRight'}
                                        />
                                    </Stack>
                                </Stack>
                            </Stack.Item>
                            {supportedFeaturesExpanded && (
                                <div className={styles.supportedFeaturesList}>
                                    <ul className={styles.featureList}>
                                        {parsedAnswer.supportedFeatures.map((feature, idx) => (
                                            <li key={idx} className={styles.featureItem}>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </Stack>
                    )}

                    {/* Feedback Buttons - Moved to bottom right */}
                    {FEEDBACK_ENABLED && answer.message_id !== undefined && (
                        <Stack.Item className={styles.feedbackButtonsContainer}>
                            <Stack horizontal horizontalAlign="end">
                                <ThumbLike20Filled
                                    aria-hidden="false"
                                    aria-label="Like this response"
                                    onClick={() => onLikeResponseClicked()}
                                    style={
                                        feedbackState === Feedback.Positive ||
                                        appStateContext?.state.feedbackState[answer.message_id] ===
                                        Feedback.Positive
                                            ? { color: 'darkgreen', cursor: 'pointer' }
                                            : { color: 'slategray', cursor: 'pointer' }
                                    }
                                />
                                <ThumbDislike20Filled
                                    aria-hidden="false"
                                    aria-label="Dislike this response"
                                    onClick={() => onDislikeResponseClicked()}
                                    style={
                                        feedbackState !== Feedback.Positive &&
                                        feedbackState !== Feedback.Neutral &&
                                        feedbackState !== undefined
                                            ? { color: 'darkred', cursor: 'pointer' }
                                            : { color: 'slategray', cursor: 'pointer' }
                                    }
                                />
                            </Stack>
                        </Stack.Item>
                    )}
                </Stack>
            </Stack>

            <Dialog
                onDismiss={() => {
                    resetFeedbackDialog();
                    setFeedbackState(Feedback.Neutral);
                }}
                hidden={!isFeedbackDialogOpen}
                styles={{
                    main: [
                        {
                            selectors: {
                                '@media (min-width: 480px)': {
                                    maxWidth: '600px',
                                    background: '#FFFFFF',
                                    boxShadow: '0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)',
                                    borderRadius: '8px',
                                    maxHeight: '600px',
                                    minHeight: '100px',
                                },
                            },
                        },
                    ],
                }}
                dialogContentProps={{
                    title: 'Submit Feedback',
                    showCloseButton: true,
                }}>
                <Stack tokens={{ childrenGap: 4 }}>
                    <div>Your feedback will improve this experience.</div>
                    {!showReportInappropriateFeedback ? <UnhelpfulFeedbackContent /> : <ReportInappropriateFeedbackContent />}
                    <TextField
                        placeholder="Please provide more information"
                        maxLength={2048}
                        multiline
                        onChange={handleOtherTextChange}
                        value={otherFeedbackText}
                        errorMessage={
                            negativeFeedbackList.includes(Feedback.OtherUnhelpful) && !otherFeedbackText
                                ? 'This field is required if you select "Other".'
                                : ''
                        }
                    />
                    <div>By pressing submit, your feedback will be visible to the application owner.</div>
                    <div className={styles.submitContainer}>
                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={
                                negativeFeedbackList.length < 1 ||
                                (negativeFeedbackList.includes(Feedback.OtherUnhelpful) && !otherFeedbackText)
                            }
                            onClick={onSubmitNegativeFeedback}>
                            Submit
                        </button>
                    </div>
                </Stack>
            </Dialog>
        </>
    );

}
